import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { ArrowUpRight, ArrowDownRight, Lightbulb, Mail, X } from "lucide-react";
import { api, money, fullMoney } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { ChatPanel } from "@/components/ChatPanel";
import { WeeklyUpdateCard } from "@/components/WeeklyUpdateCard";

const Card = ({ className = "", children, testid }) => (
  <div
    data-testid={testid}
    className={`relative bg-[#141220]/90 backdrop-blur-xl border border-[#262336] p-8 rounded-[26px] shadow-[0_12px_44px_rgba(0,0,0,0.55)] ${className}`}
  >
    {children}
  </div>
);

const Label = ({ children }) => (
  <p className="font-mono-fin text-[10px] uppercase tracking-[0.22em] text-[#9A93AD]">{children}</p>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [showImprove, setShowImprove] = useState(false);
  const [digest, setDigest] = useState(null);
  const [showDigest, setShowDigest] = useState(false);

  const load = () => api.get("/dashboard").then(({ data }) => setData(data));

  useEffect(() => {
    load();
  }, []);

  if (!data)
    return (
      <Shell>
        <p className="font-mono-fin text-xs uppercase tracking-[0.2em] text-[#9A93AD]">Loading</p>
      </Shell>
    );

  if (!data.has_data)
    return (
      <Shell>
        <div className="max-w-xl rise" data-testid="empty-state">
          <p className="font-mono-fin text-[11px] font-semibold uppercase tracking-[0.28em] grad-text">No valuations yet</p>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl font-black tracking-tight text-white">Start with your baseline.</h1>
          <p className="mt-6 text-[#B4ADC6] leading-relaxed">
            Run your first valuation. Three minutes of inputs gives you an adjusted EBITDA, a defensible
            multiple and a range you can take to a banker.
          </p>
          <Link
            to="/new"
            data-testid="empty-state-cta"
            className="grad-btn mt-10 inline-flex px-8 py-4 rounded-full text-white font-bold"
          >
            Start my valuation
          </Link>
        </div>
        <ChatPanel />
      </Shell>
    );

  const v = data.latest;
  const helped = v.factors.filter((f) => f.impact > 0);
  const hurt = v.factors.filter((f) => f.impact < 0);
  const neutral = v.factors.filter((f) => f.impact === 0);

  const openDigest = async () => {
    if (!digest) {
      const { data } = await api.get("/digest");
      setDigest(data);
    }
    setShowDigest(true);
  };

  return (
    <Shell>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card testid="valuation-hero" className="md:col-span-2 flex flex-col items-center text-center rise">
          <Label>Current valuation, market case</Label>
          <p className="mt-4 font-display text-5xl sm:text-6xl font-black tracking-tight grad-text" data-testid="current-valuation">
            {fullMoney(v.scenarios.market)}
          </p>
          <p className="mt-5 font-mono-fin text-sm font-semibold text-[#FFC93C]" data-testid="valuation-summary">
            {v.summary}
          </p>
          <div className="mt-8 pt-8 border-t border-[#262336] w-full">
            <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-white" data-testid="company-name">
              {v.business_name}
            </h1>
            <p className="mt-2 font-mono-fin text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9A93AD]">
              {v.inputs.industry}
            </p>
          </div>
          <div className="mt-8 grid grid-cols-3 gap-4 border-t border-[#262336] pt-6 w-full">
            {[
              ["Conservative", v.scenarios.conservative],
              ["Market", v.scenarios.market],
              ["Optimistic", v.scenarios.optimistic],
            ].map(([k, val]) => (
              <div key={k} data-testid={`scenario-${k.toLowerCase()}`}>
                <p className="font-mono-fin text-[9px] font-semibold uppercase tracking-[0.18em] text-[#9A93AD]">{k}</p>
                <p className="mt-2 font-mono-fin text-xl font-semibold text-white">{money(val)}</p>
              </div>
            ))}
          </div>
        </Card>

        <Card testid="trend-card" className="md:col-span-2 md:row-span-2 rise" >
          <div className="flex items-start justify-between">
            <div>
              <Label>Valuation trend</Label>
              <p className="mt-3 font-display text-2xl font-bold text-white">{data.count} {data.count === 1 ? "entry" : "entries"}</p>
            </div>
            <button
              onClick={openDigest}
              data-testid="digest-button"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#262336] bg-[#1C1930] text-xs font-semibold text-[#E9E4F5] hover:bg-[#E1306C] hover:text-white transition-colors duration-200"
            >
              <Mail size={14} /> Weekly digest
            </button>
          </div>
          <div className="mt-10 h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.trend} margin={{ left: 4, right: 8, top: 8 }}>
                <defs>
                  <linearGradient id="marketLine" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#FFC93C" />
                    <stop offset="50%" stopColor="#FF6B35" />
                    <stop offset="100%" stopColor="#E1306C" />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontFamily: "IBM Plex Mono", fontSize: 10, fill: "#9A93AD" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={money} tick={{ fontFamily: "IBM Plex Mono", fontSize: 10, fill: "#9A93AD" }} axisLine={false} tickLine={false} width={54} />
                <Tooltip formatter={(x) => fullMoney(x)} contentStyle={{ background: "#141220", border: "1px solid #3A3550", borderRadius: 8, fontFamily: "IBM Plex Mono", fontSize: 12, color: "#F3F0FA" }} labelStyle={{ color: "#9A93AD" }} />
                <Line type="monotone" dataKey="conservative" stroke="#6D6785" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="market" stroke="url(#marketLine)" strokeWidth={3} dot={{ r: 4, fill: "#E1306C", stroke: "#0A0910", strokeWidth: 2 }} />
                <Line type="monotone" dataKey="optimistic" stroke="#A22BE2" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-6 flex gap-6 font-mono-fin text-[10px] font-semibold uppercase tracking-[0.18em] text-[#6D6785]">
            <span className="inline-flex items-center gap-2"><span className="h-[2px] w-4 bg-[#6D6785]" /> Conservative</span>
            <span className="inline-flex items-center gap-2 text-[#E1306C]"><span className="h-[2px] w-4 bg-[#E1306C]" /> Market</span>
            <span className="inline-flex items-center gap-2 text-[#A22BE2]"><span className="h-[2px] w-4 bg-[#A22BE2]" /> Optimistic</span>
          </div>
        </Card>

        <Card testid="multiple-card" className="md:col-span-2 rise">
          <div className="grid grid-cols-3 gap-6">
            <div>
              <Label>Baseline</Label>
              <p className="mt-3 font-mono-fin text-2xl font-semibold text-white">{v.base_multiple}x</p>
            </div>
            <div>
              <Label>Adjustment</Label>
              <p className={`mt-3 font-mono-fin text-2xl font-semibold ${v.total_adjustment >= 0 ? "text-[#3DDC97]" : "text-[#FF6B35]"}`}>
                {v.total_adjustment >= 0 ? "+" : ""}{v.total_adjustment}x
              </p>
            </div>
            <div>
              <Label>Adjusted</Label>
              <p className="mt-3 font-mono-fin text-2xl font-bold grad-text" data-testid="adjusted-multiple">{v.adjusted_multiple}x</p>
            </div>
          </div>
          <div className="mt-8 border-t border-[#262336] pt-6">
            <Label>Adjusted EBITDA</Label>
            <p className="mt-3 font-display text-3xl font-black text-white" data-testid="adjusted-ebitda">{fullMoney(v.adjusted_ebitda)}</p>
          </div>
        </Card>

        <WeeklyUpdateCard latest={v} onSaved={() => { setDigest(null); load(); }} />

        <Card testid="factors-card" className="md:col-span-4 rise">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <Label>What moved your multiple</Label>
              <p className="mt-3 font-display text-2xl font-bold text-white">Factor breakdown</p>
            </div>
            <button
              onClick={() => setShowImprove(true)}
              data-testid="improve-button"
              className="grad-btn inline-flex items-center gap-2 px-6 py-3 rounded-full text-white text-sm font-bold"
            >
              <Lightbulb size={16} /> How can I improve my valuation?
            </button>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-10">
            {[["Helped", helped, "#3DDC97"], ["Hurt", hurt, "#FF6B35"], ["Neutral", neutral, "#9A93AD"]].map(([title, list, color]) => (
              <div key={title} data-testid={`factors-${String(title).toLowerCase()}`}>
                <p className="font-mono-fin text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color }}>
                  {title} ({list.length})
                </p>
                <div className="mt-5 space-y-5">
                  {list.map((f, i) => (
                    <div key={f.key} className="rise" style={{ animationDelay: `${i * 60}ms` }}>
                      <div className="flex items-baseline justify-between gap-4">
                        <p className="text-sm font-semibold text-white">{f.label}</p>
                        <span className="font-mono-fin text-sm font-bold" style={{ color }}>
                          {f.impact > 0 ? "+" : ""}{f.impact}x
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-[#9A93AD] leading-relaxed">{f.detail}</p>
                      <p className="mt-1 text-xs text-[#9A93AD]/80 italic">{f.note}</p>
                    </div>
                  ))}
                  {list.length === 0 && <p className="text-xs text-[#9A93AD]">None</p>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {showImprove && (
        <div className="fixed inset-0 z-50 bg-[#05040A]/80 backdrop-blur-md flex items-start justify-center overflow-y-auto p-6" data-testid="improve-modal">
          <div className="grad-ring bg-[#141220] max-w-2xl w-full mt-16 p-10 rounded-[26px] shadow-[0_30px_90px_rgba(0,0,0,0.8)] rise">
            <div className="flex items-start justify-between">
              <div>
                <Label>Value creation plan</Label>
                <h2 className="mt-3 font-display text-3xl font-black tracking-tight grad-text">Fix these first</h2>
              </div>
              <button onClick={() => setShowImprove(false)} data-testid="improve-close" className="text-[#9A93AD] hover:text-[#FF6B35] transition-colors duration-200">
                <X size={20} />
              </button>
            </div>
            <div className="mt-10 space-y-8">
              {v.improvements.length === 0 && (
                <p className="text-sm text-[#9A93AD]">Every factor is already working in your favour. Focus on growing adjusted EBITDA.</p>
              )}
              {v.improvements.map((imp, i) => (
                <div key={imp.key} className="border-l-2 border-[#FF6B35] pl-6 rise" style={{ animationDelay: `${i * 70}ms` }}>
                  <div className="flex items-baseline justify-between gap-4">
                    <p className="font-display text-lg font-bold text-white">{imp.title}</p>
                    <span className="font-mono-fin text-xs font-semibold text-[#FF6B35]">now {imp.current_impact > 0 ? "+" : ""}{imp.current_impact}x</span>
                  </div>
                  <p className="mt-2 text-sm text-[#9A93AD] leading-relaxed">{imp.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showDigest && digest && (
        <div className="fixed inset-0 z-50 bg-[#05040A]/80 backdrop-blur-md flex items-start justify-center overflow-y-auto p-6" data-testid="digest-modal">
          <div className="grad-ring bg-[#141220] max-w-2xl w-full mt-16 p-10 rounded-[26px] shadow-[0_30px_90px_rgba(0,0,0,0.8)] rise">
            <div className="flex items-start justify-between">
              <div>
                <Label>{digest.send_schedule}</Label>
                <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-white">{digest.subject}</h2>
              </div>
              <button onClick={() => setShowDigest(false)} data-testid="digest-close" className="text-[#9A93AD] hover:text-[#FF6B35] transition-colors duration-200">
                <X size={20} />
              </button>
            </div>
            <div className="mt-10 space-y-8">
              <div>
                <Label>Latest valuation</Label>
                <p className="mt-3 font-display text-4xl font-black grad-text">{fullMoney(digest.valuation.market)}</p>
                <p className="mt-2 font-mono-fin text-xs text-[#9A93AD]">{digest.summary}</p>
                {digest.change_vs_previous !== null && (
                  <p className={`mt-3 inline-flex items-center gap-1 font-mono-fin text-xs ${digest.change_vs_previous >= 0 ? "text-[#3DDC97]" : "text-[#FF6B35]"}`}>
                    {digest.change_vs_previous >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                    {money(Math.abs(digest.change_vs_previous))} vs previous entry
                  </p>
                )}
              </div>
              <div className="border-t border-[#262336] pt-8">
                <Label>{digest.industry} snapshot · baseline {digest.industry_baseline}x</Label>
                <p className="mt-3 text-sm text-[#9A93AD] leading-relaxed">{digest.industry_snapshot}</p>
              </div>
              <div className="border-t border-[#262336] pt-8">
                <Label>Key multiple drivers</Label>
                <div className="mt-4 space-y-3">
                  {digest.key_drivers.map((d) => (
                    <div key={d.key} className="flex items-baseline justify-between gap-4">
                      <p className="text-sm text-[#F3F0FA]">{d.label}</p>
                      <span className={`font-mono-fin text-sm ${d.impact >= 0 ? "text-[#3DDC97]" : "text-[#FF6B35]"}`}>
                        {d.impact > 0 ? "+" : ""}{d.impact}x
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ChatPanel />
    </Shell>
  );
}
